import { DateTime } from 'luxon';
import { Instance, applySnapshot, flow, getParent, types } from 'mobx-state-tree';

import {
	acceptCgStudent,
	declineCgStudent,
	getCgStudent,
	loginByCgStudent,
	removeCgStudent,
	removeCgStudentFromCoordinator,
	saveCgStudent,
} from '@common/api/cgStudents';
import { deleteMedia } from '@common/api/media';
import handleApiErrors from '@common/helpers/handleApiErrors';
import ConsultationProfileModel from '@common/models/Consultations/ConsultationProfile';
import Parent from '@common/models/Users/Parents/Parent';
import User, { IUserModel } from '@common/models/Users/User';

import { IUsersModel } from '../Users';
import CgAssessmentResult from './CgAssessmentResult';

const Test = types.model('Test', {
	id: types.identifierNumber,
	name: types.maybeNull(types.string),
	rank: types.maybeNull(types.string),
	started_at: types.maybeNull(types.string),
});

const CgStudentTestResult = types.model('CgStudentTestResult', {
	id: types.maybeNull(types.number),
	user_id: types.maybeNull(types.number),
	riasec_state: types.maybeNull(types.string),
	riasec_letter: types.maybeNull(types.string),
	riasec_results: types.optional(types.maybeNull(types.frozen()), null),
	created_at: types.maybeNull(types.string),
	report_public_url: types.maybe(types.maybeNull(types.string)),
});

const CgStudentUser = types.model('CgStudentUser', {
	id: types.optional(types.maybeNull(types.number), null),
	first_name: types.optional(types.maybeNull(types.string), ''),
	last_name: types.optional(types.maybeNull(types.string), ''),
	email: types.optional(types.maybeNull(types.string), ''),
	referral_code: types.optional(types.maybeNull(types.string), ''),
	tags: types.optional(types.array(types.string), []),
});

const CgStudentClass = types.model('CgStudentClass', {
	id: types.maybeNull(types.number),
	name: types.maybeNull(types.string),
	c_g_school_id: types.maybeNull(types.number),
	c_g_coordinator_id: types.maybeNull(types.number),
	code: types.maybeNull(types.string),
});

const CgStudentActivity = types.model('CgStudentActivity', {
	id: types.maybeNull(types.number),
	user_id: types.maybeNull(types.number),
	type: types.maybeNull(types.string),
	status: types.maybeNull(types.string),
	steps_completed: types.maybeNull(types.number),
	total_steps: types.maybeNull(types.number),
	results: types.optional(types.maybeNull(types.frozen()), null),
	created_at: types.maybeNull(types.string),
	updated_at: types.maybeNull(types.string),
});

const CgStudentWorkshopPdf = types.model('CgStudentWorkshopPdf', {
	id: types.maybeNull(types.number),
	user_id: types.maybeNull(types.number),
	pdf_url: types.maybeNull(types.string),
	is_published: types.optional(types.boolean, false),
	created_at: types.maybeNull(types.string),
	updated_at: types.maybeNull(types.string),
});

const CgStudentParentUser = types.model('CgStudentParentUser', {
	id: types.maybeNull(types.number),
	first_name: types.maybeNull(types.string),
	last_name: types.maybeNull(types.string),
	email: types.maybeNull(types.string),
	tags: types.optional(types.array(types.string), []),
});

const CgStudentParent = types.model('CgStudentParent', {
	id: types.maybeNull(types.number),
	user_id: types.maybeNull(types.number),
	user: types.maybeNull(CgStudentParentUser),
});

const CgStudentCounselorUser = types.model('CgStudentCounselorUser', {
	id: types.maybeNull(types.number),
	first_name: types.maybeNull(types.string),
	last_name: types.maybeNull(types.string),
	email: types.maybeNull(types.string),
});

const CgStudentCounselor = types.model('CgStudentCounselor', {
	id: types.maybeNull(types.number),
	user: types.maybeNull(CgStudentCounselorUser),
});

// @ts-ignore
const CgStudent = types
	.compose(
		User,
		types.model({
			c_g_school_id: types.maybeNull(types.number),
			c_g_class_id: types.maybeNull(types.number),
			class: types.maybeNull(CgStudentClass),
			pending_c_g_class_id: types.maybeNull(types.number),
			pending_class: types.maybeNull(CgStudentClass),
			c_g_coordinator_id: types.maybeNull(types.number),
			approval_requested_at: types.maybeNull(types.string),
			approved_at: types.maybeNull(types.string),
			approved_by_c_g_coordinator_id: types.maybeNull(types.number),
			progress_percentage: types.maybeNull(types.number),
			profile_status: types.maybeNull(types.string),
			test_result: types.maybeNull(CgStudentTestResult),
			c_g_assessment_results: types.optional(types.array(CgAssessmentResult), []),
			activities: types.optional(types.array(CgStudentActivity), []),
			has_completed_all_workshop_assessments: types.optional(types.boolean, false),
			workshop_assessment_results: types.optional(types.array(types.frozen()), []),
			workshop_pdf: types.maybeNull(CgStudentWorkshopPdf),
			phone: types.optional(types.string, ''),
			user: types.maybeNull(CgStudentUser),
			cg_parents: types.optional(types.array(CgStudentParent), []),
			cg_parents_ids: types.optional(types.array(types.number), []),
			c_g_counselor_id: types.maybeNull(types.number),
			c_g_counselor: types.maybeNull(CgStudentCounselor),
			first_name: types.optional(types.maybeNull(types.string), ''),
			last_name: types.optional(types.maybeNull(types.string), ''),
			middle_name_ua: types.optional(types.maybeNull(types.string), ''),
			middle_name: types.optional(types.maybeNull(types.string), ''),
			nationality: types.optional(types.maybeNull(types.string), ''),
			gender: types.maybeNull(types.enumeration(['m', 'f'])),
			birthday_date: types.optional(types.maybeNull(types.string), ''),
			birth_certificate: types.optional(types.maybeNull(types.string), ''),
			first_name_ua: types.optional(types.maybeNull(types.string), ''),
			last_name_ua: types.optional(types.maybeNull(types.string), ''),
			link_to_deal: types.optional(types.string, ''),
			consultation: types.maybeNull(ConsultationProfileModel),
			// @ts-ignore
			parents: types.optional(types.array(types.late(() => Parent)), []),
			groups: types.optional(types.array(types.frozen()), []),
			tests: types.maybeNull(types.optional(types.array(Test), [])),
			invite_token: types.maybe(types.maybeNull(types.string)),
			tags: types.optional(types.array(types.string), []),
		}),
	)
	.named('CgStudent')
	.views((self) => ({
		get birthdayDate() {
			return self.birthday_date ? DateTime.fromISO(self.birthday_date, { zone: 'local' }) : null;
		},
	}))
	.preProcessSnapshot((snapshot) => ({
		...snapshot,
		cg_parents_ids: snapshot?.cg_parents_ids || [],
		activities: snapshot?.activities || [],
		c_g_assessment_results: snapshot?.c_g_assessment_results || [],
		workshop_assessment_results: snapshot?.workshop_assessment_results || [],
		cg_parents: snapshot?.cg_parents || [],
		c_g_counselor_id: snapshot?.c_g_counselor_id || null,
		c_g_counselor: snapshot?.c_g_counselor || null,
		phone: snapshot?.phone || '',
		link_to_deal: snapshot?.link_to_deal || '',
	}))
	.actions(() => ({
		_fetch: getCgStudent,
		_save: saveCgStudent,
		_remove: removeCgStudent,
	}))
	.actions((self) => ({
		login: flow(function* (id: any) {
			self.loading = true;
			try {
				const result = yield loginByCgStudent(id);
				return result;
			} catch (e) {
				handleApiErrors(e);
			} finally {
				self.loading = false;
			}
		}),

		changeClass: flow(function* ({ ...data }: any) {
			self.reloading = true;

			try {
				const result = yield self._save(self.id, data);
				applySnapshot(self, result);
			} catch (e) {
				handleApiErrors(e);
			} finally {
				self.reloading = false;
			}
		}),
		deleteAvatar: flow(function* () {
			yield deleteMedia({
				id: self.user_id as number,
				entity: 'users',
				media_collection: 'avatar',
			});
			yield self._fetch(self.id);
		}),
		removeFromCoordinator: flow(function* () {
			self.reloading = true;
			try {
				yield removeCgStudentFromCoordinator(self.id);
				const parent = getParent<IUsersModel>(self, 2);
				parent.removeItem(self as IUserModel);
			} catch (e) {
				self.reloading = false;
			}
		}),
		accept: flow(function* () {
			self.reloading = true;
			try {
				yield acceptCgStudent(self.id);
				const parent = getParent<IUsersModel>(self, 2);
				parent.removeItem(self as IUserModel);
			} catch (e) {
				handleApiErrors(e);
			} finally {
				self.reloading = false;
			}
		}),
		decline: flow(function* () {
			self.reloading = true;
			try {
				yield declineCgStudent(self.id);
				const parent = getParent<IUsersModel>(self, 2);
				parent.removeItem(self as IUserModel);
			} catch (e) {
				handleApiErrors(e);
			} finally {
				self.reloading = false;
			}
		}),
	}));

// @ts-ignore
export default CgStudent;

export interface ICgStudentModel extends Instance<typeof CgStudent> { }
